import { CombinedError } from '@urql/core';

import type { Skill } from '~/types/Award/skill';

import { useRewardsSuppressed } from '~/composables/unified/useRewardsSuppressed';

import { useAwardWalletBadgeDetailDataSource } from './awardWalletBadgeDataSource';

interface UseAwardWalletBadgeDetailManagerParams {
  slug: string;
}

export const useAwardWalletBadgeDetailManager = (params: Ref<UseAwardWalletBadgeDetailManagerParams>) => {
  const { t } = useI18n();
  const { badge, paginatorInfo, loading, error } = useAwardWalletBadgeDetailDataSource(params);

  watch(() => error.value, (err) => {
    if (err) {
      throw createError({
        statusCode: err.statusCode || 500,
        statusMessage: err instanceof CombinedError ? getFirstErrorMessageFromCombinedErrors(t, err) : err.message,
        fatal: true,
      });
    }
  }, { immediate: true });

  const learningAssetCollection = computed(() => badge.value?.learningAssetCollections?.[0] || null);

  const skills = computed(() => [
    ...(learningAssetCollection.value?.learningAssets?.map(asset => asset.publishedSkills || []).flat() || []),
  ].reduce((acc: Skill[], item) =>
    acc.some(skill => skill.id === item.id) ? acc : [...acc, item], []));

  const rewardsSuppressed = useRewardsSuppressed();

  // The rewards rule lives here, once, so both badge-detail mappers (the wallet view and the
  // /wallet/<slug> page) bind plain values (CP-11267): no earnable coins and no prize chip
  // on the schools product.
  const coinsAvailable = computed(() => (rewardsSuppressed.value
    ? 0
    : learningAssetCollection.value?.learningAssets
        ?.map(asset => asset.coinValue || null)
        .filter(Boolean)
        .reduce((acc, coin) => acc + (coin?.coins || 0), 0)));

  const activePrizeCampaign = computed(() => (
    !rewardsSuppressed.value && learningAssetCollection.value?.activeCampaign?.hasPrize
      ? learningAssetCollection.value.activeCampaign
      : null));

  const hasInProgress = computed(() => learningAssetCollection.value?.learningAssets?.some(asset => asset.currentUserProgress && !asset.currentUserProgress.completedAt) || false);

  const isAllCompleted = computed(() => learningAssetCollection.value?.learningAssets?.every(asset => asset.currentUserProgress?.completedAt || false) || false);

  const badgeIssuerIconPath = computed(() => badge.value?.partner?.iconFile?.url || '/images/unified/anyway-a-default.webp');

  const partner = computed(() => badge.value?.partner || null);

  return {
    badge,
    paginatorInfo,
    loading,
    skills,
    coinsAvailable,
    activePrizeCampaign,
    learningAssetCollection,
    hasInProgress,
    isAllCompleted,
    badgeIssuerIconPath,
    partner,
  };
};
